import { getRecognitionDistributions, RecognitionDistribution } from 'app/dux/programs';
import { SessionAppState } from 'app/dux/session';
import { getDelegatedOrCurrentCompanyId } from 'app/dux/session/selectors';
import { createSelector } from 'reselect';
import { initialBuilderState } from 'routes/tokens/create/dux/reducer';
import { danger } from 'shared/helper-text-helpers';
import { RosterPersonMap } from 'shared/person-search-field/PersonSearchField';
import { TokenBuilderAppState, ValidationState } from './interfaces';

type RelevantAppState = SessionAppState & TokenBuilderAppState;

export const getTokenBuilderState = (state: RelevantAppState) =>
    state.tokenBuilder[getDelegatedOrCurrentCompanyId(state)] ?? initialBuilderState;

export const getArcade = (state: RelevantAppState) => getTokenBuilderState(state).arcade;

export const getArcadeRooms = (arcadeId?: string) =>
    (state: RelevantAppState) => arcadeId ? getTokenBuilderState(state).roomsByArcadeId[arcadeId] : undefined;

export const getRoomMap = (state: RelevantAppState) => getTokenBuilderState(state).roomsById;

export const getArcades = (state: RelevantAppState) => getTokenBuilderState(state).arcadeList;

export const getCount = (state: RelevantAppState) => getTokenBuilderState(state).count;

export const getRecipientIds = (state: RelevantAppState) => getTokenBuilderState(state).recipientIds;

export const getRecipients = (state: RelevantAppState) => getTokenBuilderState(state).recipients;

export const getRecognitionDistributionsSelected = (state: RelevantAppState) =>
    getTokenBuilderState(state).recognitionDistributionsSelected;

export const getRecognitionDistributionSelected = (distributionId: string) =>
    (state: RelevantAppState) =>
        Boolean(getTokenBuilderState(state).recognitionDistributionsSelected[distributionId]);

export const getValidationErrors = (state: RelevantAppState) => getTokenBuilderState(state).validationErrors;

export const countSelectedDistributions = (state: RelevantAppState) =>
    Object.values(getRecognitionDistributionsSelected(state)).filter(Boolean).length;

export const countTotalTokens = createSelector(
    [
        getCount,
        getRecipientIds,
        getRecognitionDistributionsSelected,
    ],
    (
        count: number,
        recipientIds: string[],
        recognitionDistributionsSelected: { [recognitionDistributionId: string]: boolean },
    ) => {
        const distributionCount = Object.values(recognitionDistributionsSelected)
            .filter((selected: boolean) => selected)
            .length;
        return distributionCount * count * recipientIds.length;
    }
);

export const countEligibleTokens = createSelector(
    [
        getCount,
        getRecipientIds,
        getRecipients,
        getRecognitionDistributions,
        getRecognitionDistributionsSelected,
    ],
    (
        count: number,
        recipientIds: string[],
        recipients: RosterPersonMap,
        recognitionDistributions: { [groupId: string]: RecognitionDistribution | undefined },
        recognitionDistributionsSelected: { [recognitionDistributionId: string]: boolean },
    ) => {
        let eligibleDistributions = 0;
        for (const [distributionId, selected] of Object.entries(recognitionDistributionsSelected)) {
            if (!selected) {
                continue;
            }

            const distribution = recognitionDistributions[distributionId];

            const eligibleGroupIds = new Set(distribution?.rewardDistribution.rosterGroups.map(({ groupId }) => groupId));

            for (const recipientId of recipientIds) {
                const recipient = recipients[recipientId];
                if (recipient?.groups.some(({ id }) => eligibleGroupIds.has(id))) {
                    eligibleDistributions++;
                }
            }
        }

        return eligibleDistributions * count;
    }
);

export const calculateValidationErrors = (state: RelevantAppState): { errorMessage: string; validationErrors: ValidationState } | undefined => {

    let errorMessage: string | undefined;
    const validationErrors: ValidationState = {};

    const {
        arcade,
        recipientIds,
        recognitionDistributionsSelected,
    } = getTokenBuilderState(state);


    if (!recipientIds.length) {
        validationErrors.recipientIds = danger('This field is required');
        errorMessage = 'At least one recipient must be specified';
    }

    if (!arcade) {
        validationErrors.arcade = danger('This field is required');
        errorMessage = errorMessage ?? 'An arcade must be selected';
    }

    if (!Object.values(recognitionDistributionsSelected).some(x => x)) {
        validationErrors.recognitionDistributionsSelected = danger('At least one room must be selected');
        errorMessage = errorMessage ?? 'At least one room must be selected';
    }

    if (!errorMessage) {
        return;
    }

    return {
        errorMessage,
        validationErrors,
    };
};

